import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { SortField, SortOrder } from "@/components/candidates/CandidatesViewOptions";
import { toast } from "@/hooks/use-toast";
import { JobData } from "./useJobsData";
import { normalizeSkill } from "@/utils/skillNormalization";
import { CLOSED_JOB_STATUSES_FILTER } from "@/utils/jobs/status";

const ITEMS_PER_PAGE = 25;

export interface JobFilters {
	searchTerm?: string;
	skillsFilter?: string[];
	skillsOperator?: "AND" | "OR";
	statusFilter?: string[];
	showClosed?: boolean;
	maxDaysAgo?: number | null;
	assignedToMe?: boolean;
	currentUserId?: string | null;
	showFavoritesOnly?: boolean;
	favorites?: Set<string>;
	disablePagination?: boolean;
}

export interface UseJobsPaginatedReturn {
	jobs: JobData[];
	isLoading: boolean;
	totalCount: number;
	totalPages: number;
	currentPage: number;
	setCurrentPage: (page: number) => void;
	refetch: () => Promise<void>;
}

export const useJobsPaginated = (
	filters: JobFilters,
	sortField: SortField = "dateCreated",
	sortOrder: SortOrder = "desc"
): UseJobsPaginatedReturn => {
	const [jobs, setJobs] = useState<JobData[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [totalCount, setTotalCount] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const [orgUuid, setOrgUuid] = useState<string | null>(null);

	// Fetch organization UUID once on mount
	useEffect(() => {
		const fetchOrgUuid = async () => {
			try {
				const { data: { user } } = await supabase.auth.getUser();
				if (!user) return;

				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', user.id)
					.single();

				if (userOrg?.organization_uuid) {
					setOrgUuid(userOrg.organization_uuid);
				}
			} catch (error) {
				console.error("Error fetching organization:", error);
			}
		};

		fetchOrgUuid();
	}, []);

	const fetchJobs = useCallback(async () => {
		if (!orgUuid) {
			// Keep loading state true while waiting for orgUuid
			return;
		}

		try {
			setIsLoading(true);

			// Check if new columns exist
			const { error: columnCheckError } = await supabase
				.from('jobs')
				.select('end_date, budget_freelance, extra_lang, time_commitment, ai_summary')
				.limit(1);

			// Build query based on available columns
			const viewName = !columnCheckError ? 'jobs_with_notifications' : 'jobs';
			
			let selectQuery = `
				id, title, description, created_at, updated_at, status, 
				published, publishdate, employment_type, seniority_level, 
				location_type, hard_skills, soft_skills, education_level, 
				years_of_experience, language_requirements, culture_tags, 
				application_deadline, job_posting_date, source, assigned_recruiter,
				employment_classification, ai_analysis,
				companies!inner(id, name), 
				contacts(id, name)
			`;

			if (!columnCheckError) {
				selectQuery += `, end_date, budget_freelance, extra_lang, time_commitment, ai_summary,
					new_applications_count`;
			}

			// Start building query - APPLY ALL FILTERS IN DATABASE
			let query = supabase
				.from(viewName)
				.select(selectQuery, { count: 'exact' })
				.eq('organization_uuid', orgUuid);

			// Status filter
			const validStatusFilter = (filters.statusFilter || []).filter(s => s.trim() !== "");
			if (validStatusFilter.length > 0) {
				query = query.in('status', validStatusFilter);
			}

			// Closed status filter
			if (!filters.showClosed) {
				query = query.not('status', 'in', CLOSED_JOB_STATUSES_FILTER);
			}

			// Days filter — show jobs created or modified within the date range
			if (filters.maxDaysAgo !== null && filters.maxDaysAgo !== undefined) {
				const daysAgoDate = new Date();
				daysAgoDate.setDate(daysAgoDate.getDate() - filters.maxDaysAgo);
				const cutoff = daysAgoDate.toISOString();
				query = query.or(`created_at.gte.${cutoff},updated_at.gte.${cutoff}`);
			}

			// Assigned to me filter
			if (filters.assignedToMe && filters.currentUserId) {
				query = query.eq('assigned_recruiter', filters.currentUserId);
			}

			// Skills filter - normalize skills before querying
			// Note: jobs have skills in multiple fields (hard_skills, soft_skills, ai_analysis)
			if (filters.skillsFilter && filters.skillsFilter.length > 0) {
				const normalizedSkillsFilter = filters.skillsFilter.map(skill => normalizeSkill(skill));
				
				if (filters.skillsOperator === "OR") {
					// ANY match: check hard_skills OR soft_skills arrays
					query = query.or(`hard_skills.ov.{${normalizedSkillsFilter.join(',')}},soft_skills.ov.{${normalizedSkillsFilter.join(',')}}`);
				} else {
					// ALL match: for AND, we need both arrays to contain all skills
					query = query.overlaps('hard_skills', normalizedSkillsFilter);
				}
			}

			// Search term filter (searches title, description)
			if (filters.searchTerm && filters.searchTerm.trim() !== "") {
				const term = filters.searchTerm.trim();
				query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%`);
			}

			// Sorting
			const sortMapping: Record<SortField, string> = {
				name: 'title',
				dateCreated: 'created_at',
				matchScore: 'match_count'
			};
			const dbSortField = sortMapping[sortField] || 'created_at';
			query = query.order(dbSortField, { ascending: sortOrder === 'asc' });

			// Pagination - AFTER all filters (skipped for Kanban, with a safety cap)
			if (filters.disablePagination) {
				query = query.range(0, 999);
			} else {
				const from = (currentPage - 1) * ITEMS_PER_PAGE;
				query = query.range(from, from + ITEMS_PER_PAGE - 1);
			}

			const { data, error, count } = await query;

			if (error) {
				throw error;
			}

			// Transform data to match JobData interface
			const transformedData: JobData[] = (data || []).map((job: any) => ({
				id: job.id,
				title: job.title,
				company: {
					id: job.companies?.id || '',
					name: job.companies?.name || 'Unknown Company'
				},
				skills: [...(job.hard_skills || []), ...(job.soft_skills || [])],
				hardSkills: job.hard_skills,
				softSkills: job.soft_skills,
				createdAt: job.created_at,
				updated_at: job.updated_at,
				description: job.description,
				aiSummary: job.ai_summary,
				matchCount: job.match_count,
				contactId: job.contacts?.id,
				published: job.published,
				publishdate: job.publish_date,
				status: job.status,
				employmentType: job.employment_type,
				seniorityLevel: job.seniority_level,
				locationType: job.location_type,
				educationLevel: job.education_level,
				yearsOfExperience: job.years_of_experience,
				languageRequirements: job.language_requirements,
				cultureTags: job.culture_tags,
				applicationDeadline: job.application_deadline,
				jobPostingDate: job.job_posting_date,
				source: job.source,
				assignedRecruiter: job.assigned_recruiter,
				employmentClassification: job.employment_classification,
				aiAnalysis: job.ai_analysis,
				endDate: job.end_date,
				budgetFreelance: job.budget_freelance,
				extraLang: job.extra_lang,
				timeCommitment: job.time_commitment,
				applicationsCount: job.applications_count,
				processesCount: job.processes_count,
				newApplicationsCount: job.new_applications_count
			}));

			// Client-side favorites filter (if favorites are not in database)
			let finalData = transformedData;
			if (filters.showFavoritesOnly && filters.favorites && filters.favorites.size > 0) {
				finalData = finalData.filter(j => filters.favorites!.has(j.id));
			}

			setJobs(finalData);
			setTotalCount(count || 0);
		} catch (error) {
			console.error("Error fetching jobs:", error);
			toast({
				title: "Failed to load jobs",
				description: error instanceof Error ? error.message : "Unknown error",
				variant: "destructive",
			});
			setJobs([]);
			setTotalCount(0);
		} finally {
			setIsLoading(false);
		}
	}, [
		orgUuid,
		currentPage,
		filters.searchTerm,
		filters.skillsFilter,
		filters.skillsOperator,
		filters.statusFilter,
		filters.showClosed,
		filters.maxDaysAgo,
		filters.assignedToMe,
		filters.currentUserId,
		filters.showFavoritesOnly,
		filters.favorites,
		filters.disablePagination,
		sortField,
		sortOrder
	]);

	useEffect(() => {
		fetchJobs();
	}, [fetchJobs]);

	// Reset to page 1 when filters change
	useEffect(() => {
		setCurrentPage(1);
	}, [
		filters.searchTerm,
		filters.skillsFilter,
		filters.statusFilter,
		filters.showClosed,
		filters.maxDaysAgo,
		filters.assignedToMe,
		filters.showFavoritesOnly,
		filters.disablePagination
	]);

	const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

	return {
		jobs,
		isLoading,
		totalCount,
		totalPages,
		currentPage,
		setCurrentPage,
		refetch: fetchJobs
	};
};
